<template>
  <div id="header" v-if="headerVisible" 
  :class="[
  { 'header-title': pageTitle },
  { setting: mainPath == 'user' }
  ]">
    <div v-if="pageTitle" class="header-top-title" :class="{ setting: mainPath == 'user' }">
      {{ pageTitle }}
      <!-- price spot/futures -->
      <div id="market_type" v-if="mainPath == 'price' && subPath !== 'favorite'" :class="locale">
        <a :class="{ active: currentTab === 'spot' }" @click="currentTab = 'spot'">{{ $t('price.spot') }}</a>
        <a :class="{ active: currentTab === 'futures' }" @click="currentTab = 'futures'">{{ $t('price.future') }}</a>
      </div>
      <div class="brief-header" v-if="mainPath == 'board' && subPath !== 'notice'">
        <!-- briefing am/pm -->
        <div id="market_type" :class="locale"  class="brief-header-time">
          <a :class="{ active: briefTab === 'AM' }" @click="briefTab = 'AM'">
            {{ $t("brief['am']") }}
          </a>
          <a :class="{ active: briefTab === 'PM' }" @click="briefTab = 'PM'">
            {{ $t("brief['pm']") }}
          </a>
      </div>
       </div>
    <!-- 언어 선택 -->
      <div class="lang-type" v-if="mainPath == 'board'">
        <LanguageSetting v-show="subPath === 'brief' || subPath === 'notice' || subPath === 'ai-brief'" :availLang="availLang" :langDisplay="langDisplay" />
      </div>
    </div>

    <div v-if="mainPath !== 'user'" :class="[
      'header-menu',
      { set: checkHeader },
      mainPath,
      locale
    ]">
      
      <div class="trading-view-header" v-if="isTradingView">
        <div @click="$router.push('/price/favorite')">
          <img src="@/assets/images/dark/icon_back.png" alt="back" />
        </div>
        <h3>코인 차트</h3>
        <div></div>
      </div>

      <div v-for="link in links" :key="link.to">
        <router-link :to="link.to" class="header-link">
          <img v-if="link.to === '/price/favorite'" src="@/assets/images/price/star.png" alt="star icon"/>
          {{ link.text }}
        </router-link>
      </div>

    </div>
  </div>
</template>

<script setup>
import LanguageSetting from "@/components/board/sub/LanguageSetting.vue";

import { computed, ref, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useStore } from "vuex"; 
import api from "@/api/apiUser";
import apiBoard from "@/api/apiBoard";

// ==========================================
// 1. 초기 세팅 (Hooks)
// ==========================================
const { t, locale } = useI18n();
const route = useRoute();
const store = useStore();
const availLang = ref([]);

// ==========================================
// 2. 라우트(경로) 관련 Computed
// ==========================================
const pathSegments = computed(() => route.path.split("/"));
const mainPath = computed(() => pathSegments.value[1]);
const subPath = computed(() => pathSegments.value[2]);
const isTradingView = computed(() => route.path.startsWith("/trading-view"));
const langDisplay = computed(() => localStorage.getItem("userLang") || "Language");

// ==========================================
// 3. UI 상태 및 텍스트 (Title & Tabs)
// ==========================================
const pageTitle = computed(() => {
  const titles = {
    price: "Market",
    board: "Briefing",
    user: "Settings"
  };
  return titles[mainPath.value] || ""; 
});

// price 탭 변경
const currentTab = computed({
  get: () => store.state.tab.currentTab,
  set: (value) => store.commit("tab/SET_CURRENT_TAB", value)
});

// brief 탭 변경
const briefTab = computed({
  get: () => store.state.board.timetype,
  set: (value) => store.commit("board/setTimetype", value)
});

// ==========================================
// 4. 유저 권한 및 API 데이터 (Subscription) & 언어
// ==========================================
const isSmart = ref(true);

const langClick = (payload) => store.commit("board/langClick", payload);
const changeLoadBrief = (payload) => store.commit("board/changeLoadBrief", payload);
const changeTab = (payload) => store.commit("board/changeTab", payload);
const setTimetype = (payload) => store.commit("board/setTimetype", payload);

// 언어 리스트 불러오는 함수 
const getAvailLang = async () => {
  try {
    const result = await apiBoard.getAvailLanguages();
    if (result.isSuccess) {
      result.data.unshift({ language: "default", display: "Language" });
      availLang.value = result.data; 
    }
  } catch (error) {
    // console.log("getAvailLang", error);
    return;
  }
};

onMounted(async () => {
  try {
    const { data } = await api.getSubscribe();
    isSmart.value = data?.packagePrice >= 10000;
    await getAvailLang();
  } catch (error) {
    console.error("구독 정보를 불러오는데 실패했습니다.", error);
  }
});

watch(subPath, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    langClick({ state: false });
    changeLoadBrief({ state: false });
    changeTab("am-brief");
    setTimetype("AM");
  }
});

// ==========================================
// 5. 네비게이션 링크 생성 (Links)
// ==========================================
const alarmPaths = computed(() => {
  return isSmart.value
    ? [
        { to: "/alarm/smart-chart-alarm", text: "Smart" },
        { to: "/alarm/rsi-alarm", text: "Premium" },
        { to: "/alarm/price-alarm", text: "Price" },
        { to: "/alarm/score-alarm", text: "6.4" },
        { to: "/alarm/vol-alarm", text: "Vol" },
      ]
    : [
        { to: "/alarm/price-alarm", text: "Price" },
        { to: "/alarm/score-alarm", text: "6.4" },
        { to: "/alarm/vol-alarm", text: "Vol" },
        { to: "/alarm/rsi-alarm", text: "Premium" },
      ];
});

const links = computed(() => {
  const paths = {
    price: [
      { to: "/price/favorite", text: t('header["favorite"]') },
      { to: "/price/up", text: t('header["up"]') },
      { to: "/price/down", text: t('header["down"]') },
      { to: "/price/volume", text: t('header["volume"]') },
    ],
    alarm: alarmPaths.value,
    board: [
      { to: "/board/ai-brief", text: `AI ${t('header["brief"]')}` },
      // { to: "/board/brief", text: t('header["brief"]') },
      { to: "/board/notice", text: t('header["notice"]') },
    ],
    user: [{ to: "/user/setting", text: t('header["mypage"]') }],
  };
  return paths[mainPath.value] || [];
});

// ==========================================
// 6. 헤더 숨김 조건 (리팩토링 완료)
// ==========================================
const checkHeader = computed(() => {
  // 💡 수많은 OR(||) 조건을 배열로 묶어 깔끔하게 처리했습니다.
  const hiddenSubPaths = [
    "member-info", "recommend-info", "subscribe-info", "connect-api",
    "position", "setting-general", "confirm-ver", "plus-coin",
    "alarm-chart", "pm-brief", "am-brief", "notice-detail"
  ];
  
  return (
    hiddenSubPaths.includes(subPath.value) || 
    mainPath.value === "alarm" || 
    mainPath.value.includes("alarm-test")
  );
});

const headerVisible = computed(() => {
  if (
    mainPath.value === 'alarm' || 
    subPath?.value == 'am-brief' || 
    subPath?.value == 'pm-brief' ||
    subPath.value == 'notice-detail' ||
    (mainPath.value === 'user' && subPath.value !== 'setting')
  ) {
    return false; // 위 조건 중 하나라도 맞으면 헤더 숨김
  }

  // 위의 숨김 조건에 아무것도 해당하지 않으면 기본적으로 보여줍니다.
  return true; 
});

// ==========================================
// 7. 다국어 (Locale)
// ==========================================
const selectLang = ref(localStorage.getItem("localeLang"));

watch(locale, (newLocale) => {
  selectLang.value = newLocale;
});

</script>

<style>
.center {
  justify-content: center !important;
}

.set {
  display: none !important;
}

.around {
  justify-content: space-around !important;
}

.brief-header {
  display: flex;
  gap: 5px;
}

.lang-type {
  position: fixed;
  right: 16px;
}

.brief-header-time {
  margin-right: 45px;
}

@media (max-width: 410px) {
  #market_type.vi a, #market_type.ru a {
    padding: 4px 7px;
    font-size: 13px;
  }
}

@media (max-width: 350px) {
  #market_type.vi a, #market_type.ru a {
    font-size: 12px;
  }
}
</style>